<script setup>
import { reactive, ref } from 'vue'

const form = reactive({
  firstName: '',
  lastName: '',
  email: '',
  telephone: '',
  password: '',
  password_confirmation: '',
  notes: ''
})

const errors = reactive({
  firstName: '',
  lastName: '',
  email: '',
  telephone: '',
  password: '',
  password_confirmation: ''
})

const message = ref('')
const errorMsg = ref('')

const validateField = (field) => {
  switch (field) {
    case 'firstName':
      errors.firstName = form.firstName ? '' : 'First name is required.'
      break
    case 'lastName':
      errors.lastName = form.lastName ? '' : 'Last name is required.'
      break
    case 'email': {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      errors.email = emailRegex.test(form.email) ? '' : 'Invalid email format.'
      break
    }
    case 'telephone': {
      const telephoneRegex = /^\+?(\d{1,3})?[-.\s]?(\(?\d{3}\)?)[-.\s]?\d{3}[-.\s]?\d{4}$/
      errors.telephone = telephoneRegex.test(form.telephone)
        ? ''
        : 'Invalid telephone format.'
      break
    }
    case 'password':
      errors.password =
        form.password.length >= 8 ? '' : 'Password must be at least 8 characters.'
      break
    case 'password_confirmation':
      errors.password_confirmation =
        form.password_confirmation === form.password ? '' : 'Passwords do not match.'
      break
  }
}

const validateForm = () => {
  validateField('firstName')
  validateField('lastName')
  validateField('email')
  validateField('telephone')
  validateField('password')
  validateField('password_confirmation')

  return !Object.values(errors).some((error) => error !== '')
}

const register = async () => {
  if (!validateForm()) {
    errorMsg.value = 'Please correct the errors in the form.'
    return
  }

  try {
    const response = await $fetch('/api/auth/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }, // ✅ Ensure JSON format
      body: JSON.stringify(form) // ✅ Ensure body is properly formatted
    })

    message.value = response.message
    errorMsg.value = ''

    setTimeout(() => {
      navigateTo('/login')
    }, 3000)
  } catch (error) {
    errorMsg.value = error.statusMessage || 'Registration failed.'
  }
}
</script>

<template>
  <div class="grid grid-cols-1 lg:grid-cols-4">
    <div class="lg:col-start-2 lg:col-span-2">
      <h3 class="text-center">List Here</h3>
      <p class="text-center">The first step in listing is creating a user account.</p>
      <p class="text-center">All fields are required EXCEPT notes.</p>
      <p class="text-center">
        <RouterLink
          to="/privacy"
          class="font-semibold text-[blue] hover:text-[red]">
          Click here
        </RouterLink>
        to read our privacy policy.
      </p>
      <form
        @submit.prevent="register"
        class="pt-4 mt-2 border-2 border-gray-400 bg-neutral-100 rounded-md shadow-md">
        <div class="grid grid-cols-3 my-2">
          <label for="firstName">First Name:</label>
          <div>
            <input
              v-model="form.firstName"
              type="text"
              id="firstName"
              @blur="validateField('firstName')" />
          </div>
          <div>
            <span
              v-if="errors.firstName"
              class="error">
              {{ errors.firstName }}
            </span>
          </div>
        </div>

        <div class="grid grid-cols-3 my-2">
          <label for="lastName">Last Name:</label>
          <div>
            <input
              v-model="form.lastName"
              type="text"
              id="lastName"
              @blur="validateField('lastName')" />
          </div>
          <div>
            <span
              v-if="errors.lastName"
              class="error">
              {{ errors.lastName }}
            </span>
          </div>
        </div>

        <div class="grid grid-cols-3 my-2">
          <label for="email">Email:</label>
          <div>
            <input
              v-model="form.email"
              type="email"
              id="email"
              @blur="validateField('email')" />
          </div>
          <div>
            <span
              v-if="errors.email"
              class="error">
              {{ errors.email }}
            </span>
          </div>
        </div>

        <div class="grid grid-cols-3 my-2">
          <label for="Telephone">
            Telephone including area code and country code if outside of the US:
          </label>
          <div class="pt-5 pb-0">
            <input
              v-model="form.telephone"
              type="text"
              id="telephone"
              @blur="validateField('telephone')" />
          </div>
          <div>
            <span
              v-if="errors.telephone"
              class="error">
              {{ errors.telephone }}
            </span>
          </div>
        </div>

        <div class="grid grid-cols-3 my-2">
          <label for="password">Password:</label>
          <div>
            <input
              v-model="form.password"
              type="password"
              id="password"
              @blur="validateField('password')" />
          </div>
          <div>
            <span
              v-if="errors.password"
              class="error">
              {{ errors.password }}
            </span>
          </div>
        </div>

        <div class="grid grid-cols-3 my-2">
          <label for="password_confirmation">Confirm Password:</label>
          <div>
            <input
              v-model="form.password_confirmation"
              type="password"
              id="password_confirmation"
              @blur="validateField('password_confirmation')" />
          </div>
          <div>
            <span
              v-if="errors.password_confirmation"
              class="error">
              {{ errors.password_confirmation }}
            </span>
          </div>
        </div>
        <div class="grid grid-cols-3 my-2">
          <label for="notes">Notes:</label>
          <div>
            <input
              v-model="form.notes"
              type="text"
              id="notes" />
          </div>
        </div>
        <div class="text-center m-4">
          <button
            type="submit"
            class="w-fit border-2 bg-[blue] hover:bg-[red] text-white rounded-md p-1">
            Create User Account
          </button>
        </div>
        <p
          v-if="message"
          class="success">
          {{ message }}
        </p>
        <p
          v-if="errorMsg"
          class="error">
          {{ errorMsg }}
        </p>
      </form>
    </div>
  </div>
</template>

<style scoped>
.error {
  color: red;
  font-size: 0.875rem;
  margin-right: 0.5rem;
  margin-left: 0.5rem;
}

label {
  text-align: right;
  margin-right: 0.5rem;
}

input {
  border: 2px solid #ccc;
  border-radius: 4px;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
  background-color: floralwhite;
  width: 100%;
}
</style>
